import {
  DatePicker,
  DateTimePicker,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import { utcToZonedTime } from 'date-fns-tz';
import { usePlatform } from 'platform/PlatformHooks';
import { useUserTimeZone } from 'root/useUser';
import {
  convertToUserTz,
  convertToUserTzDate,
  populateCurrentTimeIfEmpty,
} from 'utils/dateUtils';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import React from 'react';
import { TextFieldProps } from '@mui/material';
import { IoDatasourceAttributes } from '@cloudio-saas/datasource-types';
import { addHours } from 'date-fns';
import { Button } from '@mui/material';

type Props = {
  value: string | null;
  onChange: (date: Date | null) => void;
  showTime: boolean;
  hideTodayButton?: boolean;
  renderInput: (props: TextFieldProps) => JSX.Element;
  attr: IoDatasourceAttributes | undefined;
  minDateString?: string;
  maxDateString?: string;
} & Record<string, unknown>;

export function DateInputWithTZ(props: Props) {
  const {
    value,
    onChange,
    showTime,
    hideTodayButton,
    attr,
    minDateString,
    maxDateString,
  } = props;
  const userTimeZone = useUserTimeZone();
  const platform = usePlatform();
  let newValue = value;
  const dateOnly = attr?.attributeType === 'Date';
  // always convert to user (if setup) or browser timezone
  // to handle date only fields to ignore the timezones
  if (value) {
    newValue = convertToUserTz({
      value,
      userTimeZone,
      platform,
      showTime,
      attr,
    });
  }
  if (dateOnly && newValue) {
    newValue = addHours(new Date(newValue), 12).toISOString();
  }
  let minDate = minDateString;
  let maxDate = maxDateString;
  if (minDate) {
    minDate = convertToUserTz({
      value: minDate,
      userTimeZone,
      platform,
      showTime,
      attr,
    });
    if (dateOnly && minDate) {
      minDate = addHours(new Date(minDate), 12).toISOString();
    }
  }
  if (maxDate) {
    maxDate = convertToUserTz({
      value: maxDate,
      userTimeZone,
      platform,
      showTime,
      attr,
    });
    if (dateOnly && maxDate) {
      maxDate = addHours(new Date(maxDate), 12).toISOString();
    }
  }

  const handleChange = (date: Date | null) => {
    if (date && date.getFullYear().toString().length !== 4) {
      return;
    }
    let newDate = date;
    if (newDate && !showTime) {
      newDate.setHours(0, 0, 0, 0);
    }
    if (newDate && isEmpty(value)) {
      // newly selected date is not having time portion, hence adding current time to it
      populateCurrentTimeIfEmpty(newDate, attr, showTime);
    }
    // always convert to user (if setup) or browser timezone
    // to handle date only fields to ignore the timezones
    newDate = convertToUserTzDate({
      date: newDate,
      userTimeZone,
      platform,
      showTime,
      attr,
    });

    onChange(newDate);
  };
  const renderWeekPickerDay = (
    date: Date,
    selectedDates: Array<Date | null>,
    pickersDayProps: PickersDayProps<Date>,
  ) => {
    const isToday =
      date.toDateString() ===
      utcToZonedTime(new Date().toISOString(), userTimeZone).toDateString();
    pickersDayProps.today = isToday;
    pickersDayProps.autoFocus = isEmpty(value) && isToday;
    return <PickersDay {...pickersDayProps} />;
  };
  const CustomActionBar = (actionBarProps: any) => {
    const handleSelectToday = () => {
      actionBarProps.onSetToday();
    };
    return (
      <>
        <PickersActionBar {...actionBarProps} />
        <Button
          variant="text"
          fullWidth
          disableRipple
          disableElevation
          sx={{
            mt: 1,
            mb: 0,
            borderTop: '2px solid #e0e0e0',
            borderRadius: 0,
            backgroundColor: '#d3d3d3',
            color: '#222',
            fontWeight: 800,
            fontSize: '1rem',
            minWidth: 120,
            px: 2,
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#bcbcbc',
            },
          }}
          onClick={handleSelectToday}
        >
          Today
        </Button>
      </>
    );
  };

  const newProps = {
    ...props,
    value: newValue,
    onChange: handleChange,
    // added below props to display user timezone today date
    ...(!hideTodayButton && { components: { ActionBar: CustomActionBar } }),
    ...(isNotEmpty(userTimeZone) && { renderDay: renderWeekPickerDay }),
    ...(isNotEmpty(userTimeZone) && {
      defaultCalendarMonth: utcToZonedTime(
        new Date().toISOString(),
        userTimeZone,
      ),
    }),
  };

  return showTime ? (
    <DateTimePicker
      {...newProps}
      minDate={new Date(minDate || '')}
      maxDate={new Date(maxDate || '')}
    />
  ) : (
    <DatePicker
      {...newProps}
      minDate={new Date(minDate || '')}
      maxDate={new Date(maxDate || '')}
    />
  );
}
